import { VideoPlayerResponse } from "@switcherstudio/switcher-api-client";
import { useCallback, useMemo, useState } from "react";
import { useSwitcherClient } from "../useSwitcherClient";
import { useSelector } from "react-redux";
import { RootState } from "store/reducers";
import { useCombineLoading } from "hooks/useCombineLoading";

export interface usePutCollectionOptions {
    onSuccess?: (collection: VideoPlayerResponse) => void;
    onError?: (collection: VideoPlayerResponse) => void;
}

export const usePutCollection = (
    videoPlayerId: string,
    { onSuccess, onError }: usePutCollectionOptions = {}
) => {
    const user = useSelector((state: RootState) => state.user);

    const [localCollectionResponse, setLocalCollectionResponse] =
        useState<VideoPlayerResponse>(null);

    const [initCollectionResponse, setInitCollectionResponse] =
        useState<VideoPlayerResponse>(null);

    const {
        loading: getVideoCollectionLoading,
        dispatchApiRequest: getVideoCollection
    } = useSwitcherClient((client) => client.videoPlayers_GetVideoPlayer, {
        requestImmediately: true,
        args: [videoPlayerId],
        onSuccess: (data) => {
            setLocalCollectionResponse(data);
            setInitCollectionResponse(data);
        }
    });

    const reset = useCallback((): void => {
        setLocalCollectionResponse(initCollectionResponse);
    }, [initCollectionResponse]);

    const {
        loading: updateCollectionLoading,
        dispatchApiRequest: updateCollection
    } = useSwitcherClient(
        (client) => client.projectsVideoPlayer_PutVideoPlayer,
        {
            onSuccess: async (data) => {
                onSuccess?.(data);
                await getVideoCollection();
            },
            onError: async (data) => {
                onError?.(data);
                reset();
                await getVideoCollection();
            }
        }
    );

    const onChange = useCallback(
        <T extends keyof VideoPlayerResponse["VideoPlayer"]>(
            key: T,
            value: VideoPlayerResponse["VideoPlayer"][T]
        ) => {
            setLocalCollectionResponse((c) => {
                if (!c) return c; // still null, nothing to update

                return {
                    ...c,
                    VideoPlayer: {
                        ...c.VideoPlayer,
                        [key]: value === "" ? null : value
                    }
                };
            });
        },
        []
    );

    const hasChanges = useMemo(() => {
        if (!localCollectionResponse || !initCollectionResponse) return false;

        return (
            JSON.stringify(localCollectionResponse.VideoPlayer) !==
            JSON.stringify(initCollectionResponse.VideoPlayer)
        );
    }, [localCollectionResponse, initCollectionResponse]);

    const update = useCallback(async () => {
        await updateCollection([
            videoPlayerId,
            localCollectionResponse,
            user?.userInfo?.MetaData?.Details?.ProjectId
        ]);
    }, [localCollectionResponse, updateCollection, user, videoPlayerId]);

    // Disabled for now for performance costs
    // const unchanged = useMemo(
    //     () => deepEqual(initCollectionResponse, localCollectionResponse),
    //     [initCollectionResponse, localCollectionResponse]
    // );

    const loading = useCombineLoading(
        updateCollectionLoading,
        getVideoCollectionLoading
    );

    // useEffect(() => {
    //     if (autosave && !unchanged) {
    //         update();
    //     }
    // }, [autosave, unchanged, update]);

    return {
        loading,
        saving: updateCollectionLoading,
        hasChanges,
        collection: localCollectionResponse,
        getVideoCollection,
        reset,
        update,
        onChange
    };
};
